import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';
import { numericFilterColumn } from '../../../utils/numericFilterColumn';

const STATUS_COLORS = {
  draft:     { color: '#64748B', bg: '#F1F5F9' },
  active:    { color: '#0291EF', bg: '#EFF6FF' },
  reminding: { color: '#EA580C', bg: '#FFF7ED' },
  closed:    { color: '#9333EA', bg: '#FAF5FF' },
  activated: { color: '#16A34A', bg: '#DCFCE7' },
};

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

const useOpenEnrollmentPeriodApi = (queryParams) => {
  const { t, i18n } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [packages, setPackages] = useState([]);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    api.get('/v1/benefit-packages?pageSize=500').then((res) => {
      setPackages(res.data?.data?.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/open-enrollment-periods?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/open-enrollment-periods', normalize(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/open-enrollment-periods/${id}`, normalize(formData));
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) {
        await api.delete(`/v1/open-enrollment-periods/${id}`);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const changeStatus = useCallback(async (id, targetStatus) => {
    setFetching(true);
    try {
      await api.put(`/v1/open-enrollment-periods/${id}/status`, { targetStatus });
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('openEnrollment.name'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'status', label: t('openEnrollment.status'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: {
          names: ['draft', 'active', 'reminding', 'closed', 'activated'],
          renderValue: (v) => v ? t(`openEnrollment.status${v.charAt(0).toUpperCase()}${v.slice(1)}`, { defaultValue: v }) : '',
        },
        customBodyRender: (v) => {
          const cfg = STATUS_COLORS[v] || STATUS_COLORS.draft;
          return <StatusBadge color={cfg.color} bg={cfg.bg}>
            {t(`openEnrollment.status${v?.charAt(0).toUpperCase()}${v?.slice(1)}`, { defaultValue: v })}
          </StatusBadge>;
        },
      },
    },
    dateFilterColumn('startDate', t('openEnrollment.startDate'), i18n.language),
    dateFilterColumn('endDate', t('openEnrollment.endDate'), i18n.language),
    { name: 'defaultPackageName', label: t('openEnrollment.defaultPackage'), options: { filter: false, sort: false, customBodyRender: (v) => v ?? '-' } },
    {
      name: 'budgetCarryoverPolicy', label: t('openEnrollment.budgetCarryoverPolicy'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => t(`openEnrollment.policy${v?.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('')}`, { defaultValue: v }),
      },
    },
    { name: 'submittedSelectionCount', label: t('openEnrollment.submittedCount'), options: { filter: false, sort: true } },
    numericFilterColumn('eligibleEmployeeCount', t('openEnrollment.eligibleCount'), { decimals: 0 }),
  ], [t, i18n.language]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('openEnrollment.name'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('openEnrollment.description'), type: 'textarea', grid: 12 },
    { name: 'startDate', label: t('openEnrollment.startDate'), type: 'date', grid: 6, required: true },
    { name: 'endDate', label: t('openEnrollment.endDate'), type: 'date', grid: 6, required: true },
    {
      name: 'defaultPackageId', label: t('openEnrollment.defaultPackage'), type: 'select', grid: 6,
      options: packages, idKey: 'id', labelKey: 'name',
    },
    {
      name: 'budgetCarryoverPolicy', label: t('openEnrollment.budgetCarryoverPolicy'), type: 'select', grid: 6, required: true,
      options: [
        { id: 'reset', name: t('openEnrollment.policyReset') },
        { id: 'partial-80', name: t('openEnrollment.policyPartial80') },
        { id: 'full-carryover', name: t('openEnrollment.policyFullCarryover') },
        { id: 'feb-lock', name: t('openEnrollment.policyFebLock') },
      ],
      idKey: 'id', labelKey: 'name',
    },
  ], [t, packages]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete, changeStatus };
};

// Backend expects DonationTargets as List<string> not the form's textarea blob
function normalize(formData) {
  const out = { ...formData };
  if (out.startDate && typeof out.startDate !== 'string') out.startDate = out.startDate.toISOString();
  if (out.endDate && typeof out.endDate !== 'string') out.endDate = out.endDate.toISOString();
  if (out.defaultPackageId === '') out.defaultPackageId = null;
  return out;
}

export default useOpenEnrollmentPeriodApi;
